/* eslint-disable react/jsx-props-no-spreading */

import {annotateHTML} from '@frontapp/html-highlights-transformer';
import React, {memo, useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useToggle} from '../../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {ExtendedError} from '../../../../../../../libs/shared/core/src/helpers/types/customErrors';
import {
  CleanHtml,
  emptyCleanHtml,
  iSolemnlySwearThisHtmlIsClean,
} from '../../../../../../../libs/shared/core/src/helpers/typescript/cleanHtml';
import {LoadedMessageModel} from '../../../../../../../libs/shared/core/src/models/api/messageModel';
import {reportToBugsnag} from '../../../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {TimelineVariationsEnum} from '../../../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {ExpandButton} from '../../../../../../../libs/shared/ui/src/components/buttons/expandButton';
import {textSelection} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import {truncateHTMLString} from '../../../../../../../srcCommon/helpers/htmlTruncator';
import {markFullStorySensitive} from '../../../../../../helpers/fullstoryHelpers';
import {filterHighlightAnnotationsForMessageContent} from '../../../../../../helpers/models/annotationHelpers';
import {AnnotationSourcesEnum} from '../../../../../../models/api/annotationTypes';
import {AnnotationsHighlightViewerInheritableProps} from '../../../../../common/annotation/annotationsHighlightViewer';
import {AnnotationsHighlightViewerContainer} from '../../../../../common/annotation/annotationsHighlightViewerContainer';
import {MessageTruncatedWarning} from '../banner/messageTruncatedWarning';
import {buildAnnotationCloseTag, buildAnnotationTagOpen} from '../helpers/annotationTagsHelper';
import {MessageViewerBody} from '../messageViewerBody';

/*
 * Constants.
 */

const maxEmailTextContentLength = 100000;
const emailTagWeights = {div: 15};

/*
 * Props.
 */

export interface MessageViewerEmailBodyProps extends AnnotationsHighlightViewerInheritableProps {
  message: LoadedMessageModel;
  isExternalContentHidden: boolean;
  onOpenMessageRenderClick: () => void;
  isUserVisitor: boolean;
}

/*
 * Style.
 */

const StyledEmailDiv = styled.div`
  ${textSelection()};

  padding: 12px 16px;
  overflow: auto;

  flex: 1 1 auto;

  > p:first-child {
    -webkit-margin-before: 0;
  }

  > p:last-child {
    -webkit-margin-after: 0;
  }

  .front-signature::after {
    content: '';
    display: table;
    clear: both;
  }

  pre {
    white-space: pre-wrap;
  }
`;

const StyledExpandDiv = styled.div`
  display: flex;
  margin: 16px 0 0 -6px;
`;

/*
 * Component.
 */

export const MessageViewerEmailBody = memo<MessageViewerEmailBodyProps>((props) => {
  const {message, annotations, onOpenMessageRenderClick} = props;
  const [isQuoteVisible, toggleIsQuoteVisible] = useToggle(message.wasQuoteModified);

  // Get the message body
  const body = useMemo(() => findMessageBody(message, isQuoteVisible), [message, isQuoteVisible]);

  const filteredAnnotations = useMemo(
    () => filterHighlightAnnotationsForMessageContent(annotations),
    [annotations],
  );

  const {result: displayBody, isTruncated} = useMemo(() => {
    const result = truncateEmail(body);

    // It is better to truncate first as the html may be small -> faster annotation
    const annotatedBody = annotateHTML(result.result, filteredAnnotations, {
      onAnnotationOpen: (annotation) => buildAnnotationTagOpen(annotation),
      onAnnotationClose: (_annotation) => buildAnnotationCloseTag(),
    });

    return {isTruncated: result.isTruncated, result: annotatedBody};
  }, [filteredAnnotations, body]);

  return (
    <AnnotationsHighlightViewerContainer
      annotations={filteredAnnotations}
      onQuoteInCommentClick={props.onQuoteInCommentClick}
      hasMessageAnnotations={props.hasMessageAnnotations}
      panelId={props.panelId}
      conversationId={props.message.conversationId}
      annotationSource={AnnotationSourcesEnum.LIGHT_BODY}
    >
      <StyledEmailDiv className={markFullStorySensitive()}>
        <MessageViewerBody
          {...props}
          trustedHtml={iSolemnlySwearThisHtmlIsClean(displayBody)}
          attachments={message.attachments}
          messageId={message.id}
          isUserVisitor={props.isUserVisitor}
          onQuoteInCommentClick={props.onQuoteInCommentClick}
          timelineVariation={TimelineVariationsEnum.ASYNCHRONOUS}
        />
        {(!isTruncated || isQuoteVisible) && maybeRenderQuoteButton(message, toggleIsQuoteVisible)}
      </StyledEmailDiv>
      {isTruncated && <MessageTruncatedWarning onOpenMessageRenderClick={onOpenMessageRenderClick} />}
    </AnnotationsHighlightViewerContainer>
  );
});

function maybeRenderQuoteButton(message: LoadedMessageModel, onToggleQuoteClick: () => void) {
  const {lightBody, body, hasQuote} = message;

  // Check if there a quote to display.
  if (!lightBody || !body || !hasQuote) {
    return null;
  }

  // If so, show the toggle button.
  return (
    <StyledExpandDiv>
      <ExpandButton
        onClick={onToggleQuoteClick}
        tooltip={<Trans id="messageviewer-expand">Toggle message quote</Trans>}
      />
    </StyledExpandDiv>
  );
}

function findMessageBody(message: LoadedMessageModel, showFullBody?: boolean): CleanHtml {
  const {lightBody, body, hasQuote} = message;

  // If needed, only return the full body.
  if (showFullBody || !hasQuote || message.hasMissingBody) {
    return iSolemnlySwearThisHtmlIsClean(body.trim()) || emptyCleanHtml;
  }

  // Otherwise, give priority to the short version.
  return lightBody || body || emptyCleanHtml;
}

function truncateEmail(html: CleanHtml) {
  try {
    return truncateHTMLString(html, maxEmailTextContentLength, emailTagWeights);
  } catch (e) {
    reportToBugsnag(new ExtendedError('Failed to truncate message:', e));
    return {isTruncated: false, result: html};
  }
}
